import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import DatabaseIcon from "@mui/icons-material/Storage";
import { Box, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from "@mui/material";
import { type FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemFlatSwitch } from "../../components/UIToolkit/Clickable/SystemFlatSwitch";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useErrorSnackbar } from "../../hooks/useErrorSnackbar";
import { backendService } from "../../services/backend.service";
import { SHADOW } from "../../theme/tokens";
import { hydrateArrayBufferError } from "../../utils/arrayBufferErrorDetail";
import { downloadBlob } from "../../utils/downloadBlob";
import { PbjSubmissionProcessType } from "../../utils/models";

// Mirrors the backend's `sanitize_for_filename` (services/pbj_model/__init__.py) so the saved
// file matches the server's Content-Disposition name without reading the header cross-origin:
// lowercase ASCII words joined by `-` (NFKD-decomposed so accented letters keep their base
// letter), capped at 60 chars. Shared by both the raw and submission exports.
const deptFilenameSlug = (displayName: string): string =>
	displayName
		.normalize("NFKD")
		.replace(/[^\x20-\x7e]/g, "")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 60)
		.replace(/-+$/, "");

type Props = {
	departmentId: string;
	committedStart: string;
	committedEnd: string;
};

// Shared between the Audit and Submission Sheets tabs: the Download split menu with the
// two PBJ exports (raw data + CMS submission). Owns its own anchor / loading / snackbar
// state so each tab can drop it in without duplicating the export plumbing.
export const PbjDownloadMenu: FC<Props> = ({ departmentId, committedStart, committedEnd }) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const showErrorSnackbar = useErrorSnackbar();
	const { chosenDepartment } = useChosenDepartment();
	const [downloadAnchor, setDownloadAnchor] = useState<HTMLElement | null>(null);
	const downloadOpen = Boolean(downloadAnchor);
	const [isExporting, setIsExporting] = useState(false);
	// The CMS processType the submission files are stamped with. Merge is the normal first
	// filing (and the preset); replace is only for resubmitting a quarter CMS already accepted.
	// Session-local on purpose — a resubmission is a one-off, so it must not stick across
	// downloads and silently replace a later quarter's accepted filing.
	const [processType, setProcessType] = useState<PbjSubmissionProcessType>(PbjSubmissionProcessType.Merge);

	const rangeLabel = `${committedStart}-to-${committedEnd}`;
	// Mirrors the backend's filename token (services/pbj_model/submission_export.py): merge keeps
	// the plain name, replace is tagged so the two variants can't be mixed up in a downloads folder.
	const processFilenameToken = processType === PbjSubmissionProcessType.Merge ? "" : `-${processType}`;

	const handleRawExport = useCallback(async () => {
		setDownloadAnchor(null);
		if (isExporting) return;
		setIsExporting(true);
		try {
			const res = await backendService.downloadPbjAuditRawExport(departmentId, committedStart, committedEnd);
			const deptSlug = deptFilenameSlug(chosenDepartment.display_name);
			// DEFAULT: a display name with no ASCII alphanumerics slugs to "" — degrade to the
			// dept-less legacy filename (same rule as the backend) instead of "PBJ-RAW--…".
			const filename = deptSlug ? `PBJ-RAW-${deptSlug}-${rangeLabel}.xlsx` : `PBJ-RAW-${rangeLabel}.xlsx`;
			downloadBlob(res.data, filename, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
		} catch {
			showSnackbar(t("pbj_model.audit.raw_export.failed"), "error");
		} finally {
			setIsExporting(false);
		}
	}, [committedStart, committedEnd, rangeLabel, departmentId, isExporting, showSnackbar, t, chosenDepartment.display_name]);

	const handleSubmissionExport = useCallback(async () => {
		setDownloadAnchor(null);
		if (isExporting) return;
		setIsExporting(true);
		try {
			const res = await backendService.downloadPbjAuditSubmissionExport(departmentId, committedStart, committedEnd, processType);
			const deptSlug = deptFilenameSlug(chosenDepartment.display_name);
			// DEFAULT: a display name with no ASCII alphanumerics slugs to "" — degrade to the
			// dept-less legacy filename (same rule as the backend) instead of "pbj-submission--…".
			const filename = deptSlug
				? `pbj-submission-${deptSlug}-${rangeLabel}${processFilenameToken}.zip`
				: `pbj-submission-${rangeLabel}${processFilenameToken}.zip`;
			downloadBlob(res.data, filename, "application/zip");
		} catch (error) {
			hydrateArrayBufferError(error);
			// DEFAULT: the generic "failed" message is shown only when the response carries no
			// UIVisibleError envelope (network failure, proxy error page); a backend key such as the
			// missing facility ID / state code block replaces it inside showErrorSnackbar.
			showErrorSnackbar(t("pbj_model.audit.submission_export.failed"), { action: "pbj_submission_export" }, error);
		} finally {
			setIsExporting(false);
		}
	}, [
		committedStart,
		committedEnd,
		rangeLabel,
		departmentId,
		isExporting,
		showErrorSnackbar,
		t,
		chosenDepartment.display_name,
		processType,
		processFilenameToken,
	]);

	return (
		<>
			<SystemButton
				buttonVariant="secondary"
				size="small"
				loading={isExporting}
				leadingIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
				onClick={(e) => setDownloadAnchor(e.currentTarget)}
				disabled={isExporting}
				noFullWidthMobile
				data-testid="pbj-model-audit-download"
			>
				{t("pbj_model.audit.download")}
			</SystemButton>
			<Menu
				anchorEl={downloadAnchor}
				open={downloadOpen}
				onClose={() => setDownloadAnchor(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				slotProps={{
					paper: {
						sx: {
							mt: 1,
							// Fixed (not min) width: the process-type hint below changes with the selection,
							// and a content-sized menu would visibly grow/shrink on every toggle.
							width: 300,
							boxShadow: SHADOW.PANEL_SOFT_HOVER,
							borderRadius: 2,
						},
					},
				}}
			>
				<MenuItem onClick={handleRawExport} disabled={isExporting} dense data-testid="pbj-model-audit-download-raw">
					<ListItemIcon>
						<DatabaseIcon sx={{ fontSize: 16 }} />
					</ListItemIcon>
					<ListItemText primary={t("pbj_model.audit.download.raw")} slotProps={{ primary: { variant: "body2", sx: { fontWeight: 500 } } }} />
				</MenuItem>
				<MenuItem onClick={handleSubmissionExport} disabled={isExporting} dense data-testid="pbj-model-audit-download-submission">
					<ListItemIcon>
						<DescriptionIcon sx={{ fontSize: 16 }} />
					</ListItemIcon>
					<ListItemText primary={t("pbj_model.audit.download.submission")} slotProps={{ primary: { variant: "body2", sx: { fontWeight: 500 } } }} />
				</MenuItem>
				<Divider sx={{ my: 0.5 }} />
				{/* Scopes the submission export above (the raw export has no CMS processType). Not a
				    MenuItem on purpose — picking a process type must not close the menu or trigger a
				    download; `onKeyDown` is stopped so MenuList's arrow/typeahead nav doesn't hijack
				    keys while the switch has focus. */}
				<Box
					role="group"
					aria-label={t("pbj_model.audit.download.process_type")}
					onKeyDown={(e) => e.stopPropagation()}
					sx={{ px: 2, pt: 0.5, pb: 1, display: "flex", flexDirection: "column", gap: 0.75 }}
				>
					<Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
						{t("pbj_model.audit.download.process_type")}
					</Typography>
					<SystemFlatSwitch<PbjSubmissionProcessType>
						options={[
							{
								label: t("pbj_model.audit.download.process_type.merge"),
								value: PbjSubmissionProcessType.Merge,
								"data-testid": "pbj-model-audit-process-type-merge",
							},
							{
								label: t("pbj_model.audit.download.process_type.replace"),
								value: PbjSubmissionProcessType.Replace,
								"data-testid": "pbj-model-audit-process-type-replace",
							},
						]}
						activeIndexOrValue={processType}
						// delayCallback={0}: the switch's default 200ms delay exists for tab-style
						// navigation; here it would let a click-then-immediately-download race the state.
						onSwitch={(option) => setProcessType(option.value)}
						delayCallback={0}
						// Equal-width options + a full-width root make this a true segmented control:
						// the indicator only slides, so it never resizes mid-flight between the
						// short "Merge" and the longer "Replace".
						equalWidthOptions
						rootWidth="100%"
						data-testid="pbj-model-audit-process-type"
					/>
					{/* minHeight reserves both hints' two lines so switching slides the indicator without
					    also nudging the menu's height. Keep both strings short enough to fit. */}
					<Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.45, minHeight: "2.9em" }}>
						{t(
							processType === PbjSubmissionProcessType.Merge
								? "pbj_model.audit.download.process_type.merge.hint"
								: "pbj_model.audit.download.process_type.replace.hint",
						)}
					</Typography>
				</Box>
			</Menu>
		</>
	);
};
